import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Linkedin, Twitter, Mail, MapPin, Phone, MessageCircle } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const solutions = [
    { label: 'Voxit OmniAgent', href: '/omniagent' },
    { label: 'TotalFarm.Pro', href: '/totalfarm' },
    { label: 'FyntQuick Loan', href: '/fyntloan' },
    { label: 'SOMO Learn', href: '/somolearn' },
    { label: 'SwiftGuard Responder', href: '/swiftguard' },
  ];

  const services = [
    { label: 'App Development', href: '/services/app-development' },
    { label: 'Web Development', href: '/services/web-development' },
    { label: 'BPO Services', href: '/services/bpo' },
    { label: 'AI Solutions', href: '/services/ai-solutions' },
    { label: 'Cloud Services', href: '/services/cloud' },
  ];

  return (
    <footer className="bg-[#0a0a0f] text-white">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#fe5959]">
                <span className="text-white text-xl font-bold">V</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-tight text-white">VoxIT</span>
                <span className="text-xs leading-tight text-gray-400">Media Consulting</span>
              </div>
            </Link>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              Your partner in digital transformation. From AI solutions to agricultural technology, fintech platforms to educational tools, we build the future across multiple industries.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-white/10 hover:border-gray-500 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-white/10 hover:border-gray-500 transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-semibold text-white mb-5">Solutions</h4>
            <ul className="space-y-3">
              {solutions.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-5">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/offerings" className="text-sm text-gray-400 hover:text-white transition-colors">Offerings</Link>
              </li>
              {services.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>Stockholm, Sweden</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>Lusaka, Zambia</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>Lahore, Pakistan</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <Phone size={16} className="mt-0.5 flex-shrink-0" />
                <a href="tel:+260760930203" className="hover:text-white transition-colors">+260 760930203</a>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MessageCircle size={16} className="mt-0.5 flex-shrink-0" />
                <a href="https://wa.me/46760551806" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp: +46 760551806</a>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <Mail size={16} className="mt-0.5 flex-shrink-0" />
                <a href="mailto:sales@voxitconsulting.media" className="hover:text-white transition-colors">sales@voxitconsulting.media</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="container-custom pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-[#fe5959] to-[#d65151] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              Ready to transform your business?
            </h3>
            <p className="text-sm text-white/80">
              Join forward-thinking companies using VoxIT solutions
            </p>
          </div>
          <a
            href="#contact"
            className="bg-white text-[#050707] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            Get Started
          </a>
        </motion.div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            © {currentYear} VoxIT Media Consulting AB. All rights reserved.
          </p>
          <p className="text-xs text-gray-600 font-mono">
            Technology & Innovation for the Future
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
